import { useMemo, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  Dismiss20Regular,
  Save20Regular,
  ChevronDown20Regular,
  ArrowSwap20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  importService,
  PreviewRow,
  RowDecision,
} from "../../services/importService";

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    inset: "0",
    backgroundColor: "rgba(45,20,43,0.45)",
    zIndex: 200,
    display: "flex",
    justifyContent: "flex-end",
  },
  drawer: {
    width: "min(760px, 96vw)",
    height: "100%",
    backgroundColor: BRAND.white,
    boxShadow: "-12px 0 30px rgba(0,0,0,0.18)",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  headerIcon: {
    width: "34px",
    height: "34px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    color: BRAND.gold,
  },
  closeBtn: {
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.white,
    cursor: "pointer",
    ...shorthands.border("none"),
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "4px",
    paddingRight: "4px",
    display: "inline-flex",
    alignItems: "center",
    ":hover": { color: BRAND.gold },
  },
  body: {
    flexGrow: 1,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
  },
  targetRow: {
    marginBottom: "16px",
    display: "flex",
    columnGap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  targetLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  selectWrap: {
    position: "relative",
    display: "inline-block",
  },
  select: {
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "32px",
    cursor: "pointer",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("6px"),
    minWidth: "240px",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
    ":focus": {
      outlineStyle: "none",
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  selectChevron: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    color: BRAND.muted,
    pointerEvents: "none",
  },

  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 200px 1fr",
    columnGap: "12px",
    rowGap: "8px",
    alignItems: "stretch",
    fontSize: "0.88rem",
  },
  fieldHeader: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
    paddingTop: "8px",
    paddingBottom: "8px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  fieldHeaderCenter: {
    textAlign: "center",
    color: BRAND.purple,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: "4px",
  },
  cell: {
    appearance: "none",
    cursor: "pointer",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "transparent"),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    minHeight: "20px",
    overflowWrap: "break-word",
    textAlign: "left",
    width: "100%",
    boxSizing: "border-box",
    ":hover": { backgroundColor: "#f5eef6" },
  },
  cellSelected: {
    backgroundColor: "#f5eef6",
    ...shorthands.borderColor(BRAND.purple),
    color: BRAND.purple,
    fontWeight: 700,
  },
  cellDifferent: {
    ...shorthands.borderColor("#f5b3b3"),
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "0.85rem",
    paddingLeft: "6px",
    paddingRight: "6px",
  },

  footer: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: BRAND.bg,
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ":hover": { backgroundColor: BRAND.ink },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
});

interface MergeField {
  key: string;
  label: string;
  excelValue?: string;
  existingValue?: string;
}

const valueToString = (v: any): string | undefined => {
  if (v == null) return undefined;
  if (Array.isArray(v))
    return v.map((x) => valueToString(x)).filter(Boolean).join(", ");
  if (typeof v === "object") {
    if (v.value) return String(v.value);
    return JSON.stringify(v);
  }
  return String(v);
};

const buildContactFields = (excelPrimary: any, existing: any): MergeField[] => {
  const addr = excelPrimary?.defaultAddress ?? {};
  const exAddr = existing?.defaultAddress ?? {};
  const exComms: any[] = existing?.communications ?? [];
  const findCom = (type: string) =>
    exComms.find((c) => c?.type === type && c?.isDefault) ??
    exComms.find((c) => c?.type === type);
  const excelCom = (type: string) =>
    (excelPrimary?.communications ?? []).find((c: any) => c?.type === type);
  return [
    { key: "lastname", label: "Name", excelValue: excelPrimary?.lastname, existingValue: existing?.lastname },
    { key: "firstName", label: "Vorname", excelValue: excelPrimary?.firstName, existingValue: existing?.firstName },
    { key: "contactNbrExt", label: "Kundennummer", excelValue: excelPrimary?.contactNbrExt, existingValue: existing?.contactNbrExt },
    { key: "birthDate", label: "Geburtsdatum", excelValue: excelPrimary?.birthDate, existingValue: existing?.birthDate ? String(existing.birthDate).slice(0, 10) : undefined },
    { key: "legalForm", label: "Rechtsform", excelValue: excelPrimary?.legalForm, existingValue: existing?.legalForm?.legalForm },
    { key: "status", label: "Kontaktstatus", excelValue: excelPrimary?.status, existingValue: existing?.status?.status },
    { key: "street", label: "Straße", excelValue: addr.street, existingValue: exAddr?.street },
    { key: "postalCode", label: "PLZ", excelValue: addr.postalCode, existingValue: exAddr?.postalCode },
    { key: "city", label: "Ort", excelValue: addr.city, existingValue: exAddr?.city },
    { key: "email", label: "E-Mail", excelValue: excelCom("email")?.value, existingValue: findCom("email")?.value },
    { key: "phone", label: "Telefon", excelValue: excelCom("phone")?.value, existingValue: findCom("phone")?.value },
    { key: "homepage", label: "Homepage", excelValue: excelCom("homepage")?.value, existingValue: findCom("homepage")?.value },
  ];
};

const buildContractFields = (parsed: any, existing: any): MergeField[] => {
  return [
    { key: "contractNbr", label: "Vertragsnummer", excelValue: parsed?.contractNbr, existingValue: existing?.contractNbr },
    { key: "insuranceNbr", label: "Versicherungs-Nr.", excelValue: parsed?.insuranceNbr, existingValue: existing?.insuranceNbr },
    { key: "branch", label: "Zweig", excelValue: parsed?.branch, existingValue: existing?.branch },
    { key: "beginPolicyTerm", label: "Beginn", excelValue: parsed?.beginPolicyTerm, existingValue: existing?.beginPolicyTerm ? String(existing.beginPolicyTerm).slice(0, 10) : undefined },
    { key: "endPolicyTerm", label: "Ablauf", excelValue: parsed?.endPolicyTerm, existingValue: existing?.endPolicyTerm ? String(existing.endPolicyTerm).slice(0, 10) : undefined },
    { key: "premiumAnnualGross", label: "Beitrag", excelValue: parsed?.premiumGross, existingValue: existing?.premiumAnnualGross ?? existing?.annualFee },
    { key: "riskLocation", label: "Risikoort", excelValue: parsed?.riskLocation, existingValue: existing?.riskLocation },
  ];
};

interface Props {
  batchId: number;
  row: PreviewRow;
  onClose: () => void;
  onSaved: () => void;
}

export const ImportMergeDrawer: React.FC<Props> = ({
  batchId,
  row,
  onClose,
  onSaved,
}) => {
  const styles = useStyles();
  const matches = row.matches ?? [];
  const [targetId, setTargetId] = useState<number>(
    matches[0]?.id ?? row.matchIds[0] ?? 0,
  );
  const target = useMemo(
    () => matches.find((m: any) => m.id === targetId) ?? matches[0] ?? {},
    [targetId, matches],
  );
  const isContact = !!row.payload?.primary;

  const fields = useMemo<MergeField[]>(() => {
    if (isContact) return buildContactFields(row.payload?.primary, target);
    return buildContractFields(row.payload, target);
  }, [row.payload, target, isContact]);

  const initialPicks = useMemo<Record<string, "excel" | "existing">>(() => {
    const out: Record<string, "excel" | "existing"> = {};
    for (const f of fields) {
      const ex = valueToString(f.excelValue) ?? "";
      const cur = valueToString(f.existingValue) ?? "";
      out[f.key] = ex && ex !== cur ? "excel" : "existing";
    }
    return out;
  }, [fields]);
  const [picks, setPicks] = useState<Record<string, "excel" | "existing">>(
    initialPicks,
  );

  const [saving, setSaving] = useState(false);

  const onPick = (key: string, side: "excel" | "existing") =>
    setPicks((prev) => ({ ...prev, [key]: side }));

  const onSave = async () => {
    if (!targetId) return;
    setSaving(true);
    try {
      const decision: RowDecision = {
        action: "merge",
        targetId,
        fieldSources: picks,
      };
      await importService.setDecision(batchId, row.rowId, decision);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const matchOptionLabel = (m: any): string => {
    if (!m) return "";
    if (isContact) {
      const parts = [m.lastname, m.firstName].filter(Boolean);
      return `#${m.id} — ${parts.join(", ") || "—"}`;
    }
    return `#${m.id} — ${m.contractNbr ?? ""}`;
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIcon}>
              <ArrowSwap20Regular style={{ fontSize: 18 }} />
            </span>
            <h2 className={styles.title}>
              Zusammenführen — Zeile {row.rowIndex + 1}
            </h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Schließen"
          >
            <Dismiss20Regular />
          </button>
        </div>

        <div className={styles.body}>
          {matches.length > 1 && (
            <div className={styles.targetRow}>
              <span className={styles.targetLabel}>Zielsatz</span>
              <span className={styles.selectWrap}>
                <select
                  className={styles.select}
                  value={targetId}
                  onChange={(e) => setTargetId(Number(e.target.value))}
                >
                  {matches.map((m: any) => (
                    <option key={m.id} value={m.id}>
                      {matchOptionLabel(m)}
                    </option>
                  ))}
                </select>
                <ChevronDown20Regular className={styles.selectChevron} />
              </span>
            </div>
          )}

          <div className={styles.fieldGrid}>
            <span className={styles.fieldHeader}>Excel-Wert</span>
            <span className={mergeClasses(styles.fieldHeader, styles.fieldHeaderCenter)}>
              <ArrowSwap20Regular style={{ fontSize: 14 }} /> Feld
            </span>
            <span className={styles.fieldHeader}>Bestehender Wert</span>

            {fields.map((f) => {
              const ex = valueToString(f.excelValue) ?? "—";
              const cur = valueToString(f.existingValue) ?? "—";
              const different = ex !== cur && (ex !== "—" || cur !== "—");
              const sel = picks[f.key];
              return (
                <FieldRow
                  key={f.key}
                  label={f.label}
                  excel={ex}
                  existing={cur}
                  different={different}
                  selected={sel}
                  onPick={(side) => onPick(f.key, side)}
                  classes={{
                    cell: styles.cell,
                    cellSelected: styles.cellSelected,
                    cellDifferent: styles.cellDifferent,
                    fieldLabel: styles.fieldLabel,
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={saving || !targetId}
            onClick={onSave}
          >
            <Save20Regular /> {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldRow: React.FC<{
  label: string;
  excel: string;
  existing: string;
  different: boolean;
  selected: "excel" | "existing";
  onPick: (side: "excel" | "existing") => void;
  classes: {
    cell: string;
    cellSelected: string;
    cellDifferent: string;
    fieldLabel: string;
  };
}> = ({ label, excel, existing, different, selected, onPick, classes }) => (
  <>
    <button
      type="button"
      className={
        classes.cell +
        (selected === "excel" ? " " + classes.cellSelected : "") +
        (different ? " " + classes.cellDifferent : "")
      }
      onClick={() => onPick("excel")}
    >
      {excel}
    </button>
    <span className={classes.fieldLabel}>{label}</span>
    <button
      type="button"
      className={
        classes.cell +
        (selected === "existing" ? " " + classes.cellSelected : "") +
        (different ? " " + classes.cellDifferent : "")
      }
      onClick={() => onPick("existing")}
    >
      {existing}
    </button>
  </>
);
